import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { capitalize } from 'lodash'
import Actions from '../../../../actions'
import $t from '../../../../i18n'
import ActionButton from '../../../common/ActionButton'
import { MenuRoutes } from '../../../../consts/Menu'
import {
  clinicName,
  diabetesSideMenuDisplay,
  htnSideMenuDisplay,
  isInClinic,
  mhSideMenuDisplay,
} from '../../../../utils/clinicsUtils'
import { clinicTypes, routes } from '../../../../consts'
import Alert from '../../../common/Alert'
import { getIsHmoMember } from '../../../../utils/hmoUtils'

//icons
import SearchIcon from '../../../../resources/search.png'
import FileIcon from '../../../../resources/icon.png'
import NoteIcon from '../../../../resources/s.file.png'
import HealthIcon from '../../../../resources/s.team.png'
import CardIcon from '../../../../resources/s.card2.png'

// hmoIcons
import FileIconHmo from '../../../../resources/Home.prescriptions.Hmo.png'
import NoteIconHmo from '../../../../resources/Home.File.Hmo.png'
import CareTeamHmo from '../../../../resources/Homepage.Team.Hmo.png'
import CardIconHmo from '../../../../resources/Home.card.Hmo.png'
import BenefitsIconHmo from '../../../../resources/shield-tick.png'

import './EssentialActionsSection.css'

export const EssentialActionsSection = () => {
  const dispatch = useDispatch()
  const selectedMemberId = useSelector(
    state => state.account?.selectedMember?.id
  )
  const { userDetails } = useSelector(state => state?.profile)
  const isHmoMember = getIsHmoMember(userDetails)
  const htnDisplay = useSelector(state => htnSideMenuDisplay(state))
  const mhDisplay = useSelector(state => mhSideMenuDisplay(state))
  const diabetesDisplay = useSelector(state => diabetesSideMenuDisplay(state))
  const isInHTNClinic = useSelector(state => isInClinic(state, clinicTypes.HTN))
  const isInMHClinic = useSelector(state =>
    isInClinic(state, clinicTypes.MENTAL)
  )
  const isInDMClinic = useSelector(state =>
    isInClinic(state, clinicTypes.DIABETES)
  )

  const [shouldShowAlert, setShouldShowAlert] = useState(false)

  const firstName = userDetails?.firstName

  const onHyperTensionPressed = useCallback(() => {
    switch (htnDisplay) {
      case $t('sideMenu.clinics.nextAppointments'):
        dispatch(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            routes.MENU_ROUTE.MECICAL_VISITS_FUTURE
          )
        )
        break
      case $t('sideMenu.bookFollowup'):
      case $t('sideMenu.followUpIntake'):
        dispatch(Actions.createAction(Actions.OPEN_HTN_FOLLOW_UP_BOT_SCREEN))
        break
      default:
        dispatch(Actions.createAction(Actions.HYPERTENSION_CLICKED))
        break
    }
  }, [htnDisplay])

  const onMentalHealthPressed = useCallback(() => {
    switch (mhDisplay) {
      case $t('sideMenu.clinics.nextAppointments'):
        dispatch(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            routes.MENU_ROUTE.MECICAL_VISITS_FUTURE
          )
        )
        break
      case $t('sideMenu.clinics.bookFollowup'):
        setShouldShowAlert(true)
        break
      default:
        dispatch(Actions.createAction(Actions.MENTALHEALTH_CLICKED))
        break
    }
  }, [mhDisplay])

  const onDiabetesPressed = useCallback(() => {
    switch (diabetesDisplay) {
      case $t('sideMenu.clinics.nextAppointments'):
        dispatch(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            routes.MENU_ROUTE.MECICAL_VISITS_FUTURE
          )
        )
        break
      case $t('sideMenu.followUpIntake'):
      case $t('sideMenu.clinics.bookFollowup'):
        dispatch(Actions.createAction(Actions.OPEN_DM_FOLLOW_UP_BOT_SCREEN))
        break
      default:
        dispatch(Actions.createAction(Actions.DIABETES_CLICKED))
        break
    }
  }, [diabetesDisplay])

  const onVccPressed = () => {
    dispatch(Actions.createAction(Actions.VIEW_VCC_CLICKED))
  }

  const onPrescriptionPressed = useCallback(() => {
    dispatch(
      Actions.createAction(Actions.SET_PRESCRIPTIONS_BY_ID, {
        id: selectedMemberId,
      })
    )
    dispatch(
      Actions.createAction(Actions.NAVIGATED_TO, MenuRoutes.PRESCRIPTION)
    )
  }, [dispatch, selectedMemberId])

  const onReferralsPressed = useCallback(() => {
    dispatch(
      Actions.createAction(Actions.SET_REFERRALS_BY_ID, {
        id: selectedMemberId,
      })
    )
    dispatch(Actions.createAction(Actions.NAVIGATED_TO, MenuRoutes.REFERRALS))
  }, [dispatch, selectedMemberId])

  const onWorkNotesPressed = useCallback(() => {
    dispatch(
      Actions.createAction(Actions.SET_WORK_NOTES_BY_ID, {
        id: selectedMemberId,
      })
    )
    dispatch(
      Actions.createAction(Actions.NAVIGATED_TO, MenuRoutes.DOCTOR_NOTES)
    )
  }, [dispatch, selectedMemberId])

  const onPastVisitsPressed = useCallback(() => {
    dispatch(
      Actions.createAction(Actions.SET_PAST_VISITS_LIST, {
        id: selectedMemberId,
      })
    )
    dispatch(Actions.createAction(Actions.NAVIGATED_TO, '/medical-visits/past'))
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        eventName: Actions.PAST_VISITS_CLICK,
      })
    )
  }, [selectedMemberId])

  const essentialActions = [
    {
      title: $t('homeScreen.essentialActions.managePrescriptions'),
      image: isHmoMember ? FileIconHmo : FileIcon,
      displayArrow: true,
      onPress: onPrescriptionPressed,
    },
    {
      title: $t('homeScreen.essentialActions.viewReferrals'),
      image: SearchIcon,
      hidden: isHmoMember,
      displayArrow: true,
      onPress: onReferralsPressed,
    },
    {
      title: $t('homeScreen.essentialActions.myMedicalNotes'),
      image: NoteIcon,
      hidden: isHmoMember,
      displayArrow: true,
      onPress: onWorkNotesPressed,
    },
    {
      title: $t('homeScreen.essentialActions.viewMedicalRecords'),
      image: NoteIconHmo,
      hidden: !isHmoMember,
      displayArrow: true,
      onPress: onPastVisitsPressed,
    },
    {
      title: `${clinicName(clinicTypes.HTN)} - ${htnDisplay}`,
      image: HealthIcon,
      displayArrow: true,
      onPress: onHyperTensionPressed,
      hidden: !isInHTNClinic || isHmoMember,
    },
    {
      title: `${clinicName(clinicTypes.MENTAL)} - ${mhDisplay}`,
      image: HealthIcon,
      displayArrow: true,
      onPress: onMentalHealthPressed,
      hidden: !isInMHClinic || isHmoMember,
    },
    {
      title: `${clinicName(clinicTypes.DIABETES)} - ${diabetesDisplay}`,
      image: HealthIcon,
      displayArrow: true,
      onPress: onDiabetesPressed,
      hidden: !isInDMClinic || isHmoMember,
    },
    {
      title: $t('homeScreen.essentialActions.vcc'),
      image: CardIcon,
      displayArrow: true,
      hidden: isHmoMember,
      onPress: onVccPressed,
    },
    {
      title: $t('homeScreen.essentialActions.careTeam'),
      image: CareTeamHmo,
      hidden: !isHmoMember,
      displayArrow: true,
      onPress: () => dispatch(Actions.createAction(Actions.CARE_TEAM_PRESSED)),
    },
    {
      title: $t('homeScreen.essentialActions.cards'),
      image: CardIconHmo,
      hidden: !isHmoMember,
      displayArrow: true,
      onPress: () =>
        dispatch(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            routes.PROFILE_ROUTE.ID_CARDS
          )
        ),
    },
    {
      title: $t('homeScreen.essentialActions.coverageBenefits'),
      image: BenefitsIconHmo,
      hidden: !isHmoMember,
      displayArrow: true,
      onPress: () =>
        dispatch(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            routes.PROFILE_ROUTE.COVERAGE
          )
        ),
    },
  ]

  const visibleEssentialActions = essentialActions?.filter(
    action => !action.hidden
  )

  return (
    <div className="essential-actions-section">
      <div className="section-header">
        {$t('homeScreen.essentialActions.title')}
      </div>
      <div className="essential-actions-container">
        {visibleEssentialActions?.map((action, i) => {
          return (
            <ActionButton
              key={i}
              label={action?.title}
              image={action?.image}
              className="essential-action trackable"
              displayArrow={action?.displayArrow}
              onPress={action?.onPress}
            />
          )
        })}
      </div>
      <Alert
        title={$t(
          'pages.getStarted.specialityServices.mh.followUpSupportMessage',
          { name: capitalize(firstName) }
        )}
        shouldDisplay={shouldShowAlert}
        onOk={() => setShouldShowAlert(false)}
      />
    </div>
  )
}

export default EssentialActionsSection
